import { useId } from 'react'
import { useProducts } from '../context/ProductsContext'

/**
 * Photorealistic SVG eye dropper bottle — like Latisse/bimatoprost.
 * Narrow glass bottle with a long tapered dropper tip in the cap.
 * Same label system as PeptideVial.
 */
export default function EyeDropper({
  formulaName = 'BIMATOPROST',
  formulaSubtitle = '',
  concentration = '0.03%',
  labelSize,
  wrapClassName = 'peptide-vial-float',
  rootClassName = '',
}) {
  const uid = useId().replace(/:/g, '')
  const g = (n) => `${n}-${uid}`
  const { branding } = useProducts()
  const logoSrc = branding?.logoDark?.url ?? '/logos/lookslab-logo.png'

  const W = 120, H = 220

  // Dropper tip (extends above cap)
  const TIP_X = 56, TIP_W = 8, TIP_Y = 2, TIP_H = 22
  // Dropper cap (dark, fitted)
  const CAP_X = 40, CAP_W = 40, CAP_Y = TIP_Y + TIP_H, CAP_H = 28
  // Neck
  const NCK_X = 44, NCK_W = 32, NCK_Y = CAP_Y + CAP_H, NCK_H = 8
  // Shoulder
  const SH_Y = NCK_Y + NCK_H, SH_H = 14
  // Body (narrow glass)
  const BDY_X = 26, BDY_W = 68, BDY_Y = SH_Y + SH_H, BDY_H = 120
  // Base
  const BSE_Y = BDY_Y + BDY_H, BSE_H = 8

  const MID = W / 2
  const GX1 = BDY_X, GX2 = BDY_X + BDY_W

  // Label
  const LBL_X = BDY_X + 4
  const LBL_W = BDY_W - 8
  const LBL_Y = BDY_Y + 6
  const LBL_H = BDY_H - 22
  const LMX = LBL_X + LBL_W / 2

  const nameDisplay = formulaName.toUpperCase()
  const nameFs = nameDisplay.length <= 5 ? 13
    : nameDisplay.length <= 8 ? 10
    : nameDisplay.length <= 12 ? 8
    : 6.5
  const useTextLength = nameDisplay.length > 8

  const Z1_H_LOGO = 15
  const Z2_H = nameFs + 9
  const Z3_BADGE_H = 10
  const CONTENT_H = Z2_H + 12 + 7 + 5
  const FREE = LBL_H - Z1_H_LOGO - CONTENT_H - Z3_BADGE_H
  const PAD = Math.max(2, Math.round(FREE / 2))
  const Z1_H = Z1_H_LOGO + PAD
  const Z1_Y = LBL_Y
  const Z2_Y = LBL_Y + Z1_H
  const Z3_Y = Z2_Y + Z2_H
  const Z3_H = LBL_H - Z1_H - Z2_H
  const Z2_TEXT_Y = Z2_Y + 5 + Math.round(nameFs * 0.74)
  const Z3_BOX_TOP = PAD
  const Z3_BOX_H = 12
  const Z3_LYO_Y = Z3_BOX_TOP + Z3_BOX_H + 6
  const Z3_BADGE_TOP = Z3_H - Z3_BADGE_H

  const shPath = [
    `M ${NCK_X} ${SH_Y}`,
    `L ${NCK_X + NCK_W} ${SH_Y}`,
    `L ${BDY_X + BDY_W} ${SH_Y + SH_H}`,
    `L ${BDY_X} ${SH_Y + SH_H}`,
    'Z',
  ].join(' ')

  const bsePath = [
    `M ${BDY_X} ${BSE_Y}`,
    `L ${BDY_X + BDY_W} ${BSE_Y}`,
    `L ${BDY_X + BDY_W} ${BSE_Y + BSE_H - 4}`,
    `Q ${BDY_X + BDY_W} ${BSE_Y + BSE_H} ${BDY_X + BDY_W - 5} ${BSE_Y + BSE_H}`,
    `L ${BDY_X + 5} ${BSE_Y + BSE_H}`,
    `Q ${BDY_X} ${BSE_Y + BSE_H} ${BDY_X} ${BSE_Y + BSE_H - 4}`,
    'Z',
  ].join(' ')

  // Dropper tip tapers from TIP_W at top to slightly wider at base
  const tipPath = [
    `M ${MID - TIP_W / 2 - 1} ${TIP_Y + TIP_H}`,
    `L ${MID - TIP_W / 2} ${TIP_Y + 4}`,
    `Q ${MID} ${TIP_Y} ${MID + TIP_W / 2} ${TIP_Y + 4}`,
    `L ${MID + TIP_W / 2 + 1} ${TIP_Y + TIP_H}`,
    'Z',
  ].join(' ')

  return (
    <div className={rootClassName || undefined}
      style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
      <svg
        className={wrapClassName}
        viewBox={`0 0 ${W} ${H}`}
        width={W} height={H}
        style={{ display: 'block', maxWidth: '100%', overflow: 'visible' }}
        aria-label={`${formulaName} ${concentration}`}
        role="img"
      >
        <defs>
          {/* Glass body — same as vial */}
          <linearGradient id={g('glass')} x1={GX1} y1="0" x2={GX2} y2="0" gradientUnits="userSpaceOnUse">
            <stop offset="0%"    stopColor="rgba(14,18,26,0.90)" />
            <stop offset="4%"    stopColor="rgba(44,52,68,0.50)" />
            <stop offset="10%"   stopColor="rgba(130,145,168,0.22)" />
            <stop offset="16%"   stopColor="rgba(255,255,255,0.44)" />
            <stop offset="24%"   stopColor="rgba(248,252,255,0.12)" />
            <stop offset="40%"   stopColor="rgba(218,228,242,0.04)" />
            <stop offset="50%"   stopColor="rgba(210,220,236,0.03)" />
            <stop offset="62%"   stopColor="rgba(218,228,242,0.04)" />
            <stop offset="78%"   stopColor="rgba(248,252,255,0.08)" />
            <stop offset="88%"   stopColor="rgba(145,158,180,0.20)" />
            <stop offset="95%"   stopColor="rgba(36,42,56,0.52)" />
            <stop offset="100%"  stopColor="rgba(10,14,22,0.86)" />
          </linearGradient>
          <radialGradient id={g('glow')} cx="50%" cy="44%" r="46%">
            <stop offset="0%"   stopColor="rgba(255,255,255,0.05)" />
            <stop offset="100%" stopColor="rgba(0,0,0,0)" />
          </radialGradient>

          {/* Dark dropper cap (navy/dark blue like Latisse) */}
          <linearGradient id={g('cap')} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor="#2a2e3a" />
            <stop offset="40%"  stopColor="#1e2230" />
            <stop offset="100%" stopColor="#141824" />
          </linearGradient>
          <linearGradient id={g('capx')} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%"   stopColor="rgba(0,0,0,0.20)" />
            <stop offset="20%"  stopColor="rgba(255,255,255,0.14)" />
            <stop offset="50%"  stopColor="rgba(255,255,255,0.04)" />
            <stop offset="80%"  stopColor="rgba(255,255,255,0.10)" />
            <stop offset="100%" stopColor="rgba(0,0,0,0.18)" />
          </linearGradient>

          {/* Dropper tip — translucent white plastic */}
          <linearGradient id={g('tip')} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%"   stopColor="rgba(160,168,180,0.80)" />
            <stop offset="25%"  stopColor="rgba(220,228,240,0.60)" />
            <stop offset="50%"  stopColor="rgba(240,245,252,0.40)" />
            <stop offset="75%"  stopColor="rgba(220,228,240,0.55)" />
            <stop offset="100%" stopColor="rgba(140,150,165,0.78)" />
          </linearGradient>

          {/* Label wrap — same as vial */}
          <linearGradient id={g('cylshadow')} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%"   stopColor="rgba(0,0,0,0.82)" />
            <stop offset="8%"   stopColor="rgba(0,0,0,0.55)" />
            <stop offset="16%"  stopColor="rgba(0,0,0,0.22)" />
            <stop offset="26%"  stopColor="rgba(0,0,0,0.04)" />
            <stop offset="36%"  stopColor="rgba(0,0,0,0)" />
            <stop offset="64%"  stopColor="rgba(0,0,0,0)" />
            <stop offset="76%"  stopColor="rgba(0,0,0,0.06)" />
            <stop offset="86%"  stopColor="rgba(0,0,0,0.24)" />
            <stop offset="94%"  stopColor="rgba(0,0,0,0.54)" />
            <stop offset="100%" stopColor="rgba(0,0,0,0.80)" />
          </linearGradient>
          <linearGradient id={g('cylspec')} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%"   stopColor="rgba(255,255,255,0)" />
            <stop offset="15%"  stopColor="rgba(255,255,255,0.05)" />
            <stop offset="22%"  stopColor="rgba(255,255,255,0.20)" />
            <stop offset="28%"  stopColor="rgba(255,255,255,0.06)" />
            <stop offset="38%"  stopColor="rgba(255,255,255,0)" />
            <stop offset="100%" stopColor="rgba(255,255,255,0)" />
          </linearGradient>
          <radialGradient id={g('lblrad')} cx="42%" cy="50%" r="55%">
            <stop offset="0%"   stopColor="rgba(255,255,255,0.18)" />
            <stop offset="100%" stopColor="rgba(0,0,0,0)" />
          </radialGradient>
          <linearGradient id={g('topedge')} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor="rgba(0,0,0,0.22)" />
            <stop offset="100%" stopColor="rgba(0,0,0,0)" />
          </linearGradient>
          <linearGradient id={g('botedge')} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor="rgba(0,0,0,0)" />
            <stop offset="100%" stopColor="rgba(0,0,0,0.20)" />
          </linearGradient>

          <clipPath id={g('lblclip')}>
            <rect x={LBL_X} y={LBL_Y} width={LBL_W} height={LBL_H} rx="2" />
          </clipPath>
          <clipPath id={g('bclip')}>
            <rect x={BDY_X} y={BDY_Y} width={BDY_W} height={BDY_H} />
          </clipPath>

          <radialGradient id={g('shadow')} cx="50%" cy="12%" rx="50%" ry="100%">
            <stop offset="0%"   stopColor="rgba(0,0,0,0.48)" />
            <stop offset="50%"  stopColor="rgba(0,0,0,0.15)" />
            <stop offset="100%" stopColor="rgba(0,0,0,0)" />
          </radialGradient>
        </defs>

        {/* Ground shadow */}
        <ellipse cx={MID} cy={BSE_Y + BSE_H + 8} rx={32} ry={5} fill={`url(#${g('shadow')})`} />

        {/* BASE */}
        <path d={bsePath} fill={`url(#${g('glass')})`} stroke="rgba(10,13,21,0.55)" strokeWidth="0.5" />
        <path d={bsePath} fill={`url(#${g('glow')})`} stroke="none" />

        {/* BODY */}
        <rect x={BDY_X} y={BDY_Y} width={BDY_W} height={BDY_H} fill={`url(#${g('glass')})`} />
        <rect x={BDY_X} y={BDY_Y} width={BDY_W} height={BDY_H} fill={`url(#${g('glow')})`} />
        <line x1={BDY_X + 0.5} y1={BDY_Y} x2={BDY_X + 0.5} y2={BDY_Y + BDY_H}
          stroke="rgba(162,180,205,0.30)" strokeWidth="0.7" />
        <line x1={BDY_X + BDY_W - 0.5} y1={BDY_Y} x2={BDY_X + BDY_W - 0.5} y2={BDY_Y + BDY_H}
          stroke="rgba(10,14,22,0.55)" strokeWidth="0.7" />

        {/* THREE-ZONE LABEL */}
        <g clipPath={`url(#${g('bclip')})`}>
          <g clipPath={`url(#${g('lblclip')})`}>
            <rect x={LBL_X} y={Z1_Y} width={LBL_W} height={Z1_H} fill="#f5f4f1" />
            <rect x={LBL_X} y={Z1_Y} width={LBL_W} height={Z1_H} fill={`url(#${g('lblrad')})`} />
            <image href={logoSrc}
              x={LBL_X} y={Z1_Y + (Z1_H - Z1_H_LOGO) / 2}
              width={LBL_W} height={Z1_H_LOGO}
              preserveAspectRatio="xMidYMid meet" />

            <rect x={LBL_X} y={Z2_Y} width={LBL_W} height={Z2_H} fill="#111111" />
            <text x={LMX} y={Z2_TEXT_Y} textAnchor="middle"
              fontFamily="'Inter','Helvetica Neue',sans-serif"
              fontSize={nameFs} fontWeight="900" letterSpacing="0.01em" fill="#ffffff"
              {...(useTextLength ? { textLength: LBL_W - 6, lengthAdjust: 'spacingAndGlyphs' } : {})}>
              {nameDisplay}
            </text>

            <rect x={LBL_X} y={Z3_Y} width={LBL_W} height={Z3_H} fill="#f5f4f1" />
            <rect x={LBL_X} y={Z3_Y} width={LBL_W} height={Z3_H} fill={`url(#${g('lblrad')})`} />
            <rect x={LBL_X + 5} y={Z3_Y + Z3_BOX_TOP} width={40} height={Z3_BOX_H}
              fill="none" stroke="#1a1a1a" strokeWidth="1.0" rx="0.5" />
            <text x={LBL_X + 25} y={Z3_Y + Z3_BOX_TOP + Math.round(Z3_BOX_H * 0.72)}
              textAnchor="middle" fontFamily="'Inter','Helvetica Neue',sans-serif"
              fontSize="8" fontWeight="800" letterSpacing="0.01em" fill="#111111">
              {(labelSize ?? concentration).toUpperCase()}
            </text>
            <text x={LBL_X + 5} y={Z3_Y + Z3_LYO_Y} textAnchor="start"
              fontFamily="'Inter','Helvetica Neue',sans-serif" fontSize="4.5" fontWeight="600" fill="#555555"
              textLength={40} lengthAdjust="spacingAndGlyphs">OPHTHALMIC SOL.</text>
            <rect x={LBL_X} y={Z3_Y + Z3_BADGE_TOP} width={LBL_W} height={Z3_BADGE_H} fill="#111111" />
            <text x={LMX} y={Z3_Y + Z3_BADGE_TOP + 7} textAnchor="middle"
              fontFamily="'Inter','Helvetica Neue',sans-serif"
              fontSize="4.8" fontWeight="700" letterSpacing="0.08em" fill="#efefef">
              FOR LABORATORY USE ONLY
            </text>

            <rect x={LBL_X} y={LBL_Y} width={LBL_W} height={LBL_H} fill={`url(#${g('cylshadow')})`} />
            <rect x={LBL_X} y={LBL_Y} width={LBL_W} height={LBL_H} fill={`url(#${g('cylspec')})`} />
            <rect x={LBL_X} y={LBL_Y} width={LBL_W} height={8} fill={`url(#${g('topedge')})`} />
            <rect x={LBL_X} y={LBL_Y + LBL_H - 8} width={LBL_W} height={8} fill={`url(#${g('botedge')})`} />
          </g>
        </g>

        {/* SHOULDER */}
        <path d={shPath} fill={`url(#${g('glass')})`} stroke="none" />
        <path d={shPath} fill={`url(#${g('glow')})`} stroke="none" />
        <line x1={NCK_X} y1={SH_Y} x2={NCK_X + NCK_W} y2={SH_Y}
          stroke="rgba(155,172,196,0.18)" strokeWidth="0.5" />

        {/* NECK */}
        <rect x={NCK_X} y={NCK_Y} width={NCK_W} height={NCK_H} fill={`url(#${g('glass')})`} />
        <line x1={NCK_X + 0.4} y1={NCK_Y} x2={NCK_X + 0.4} y2={NCK_Y + NCK_H}
          stroke="rgba(155,172,196,0.25)" strokeWidth="0.5" />
        <line x1={NCK_X + NCK_W - 0.4} y1={NCK_Y} x2={NCK_X + NCK_W - 0.4} y2={NCK_Y + NCK_H}
          stroke="rgba(10,14,22,0.48)" strokeWidth="0.5" />

        {/* CAP (dark dropper cap) */}
        <rect x={CAP_X} y={CAP_Y} width={CAP_W} height={CAP_H} rx="3"
          fill={`url(#${g('cap')})`} />
        <rect x={CAP_X} y={CAP_Y} width={CAP_W} height={CAP_H} rx="3"
          fill={`url(#${g('capx')})`} />
        <rect x={CAP_X + 4} y={CAP_Y + 2} width={CAP_W - 8} height={CAP_H * 0.32} rx="2"
          fill="rgba(255,255,255,0.10)" />
        <rect x={CAP_X} y={CAP_Y} width={CAP_W} height={CAP_H} rx="3"
          fill="none" stroke="rgba(8,10,18,0.70)" strokeWidth="0.6" />
        {/* Cap screw line */}
        <line x1={CAP_X} y1={CAP_Y + CAP_H - 7} x2={CAP_X + CAP_W} y2={CAP_Y + CAP_H - 7}
          stroke="rgba(255,255,255,0.08)" strokeWidth="0.6" />

        {/* DROPPER TIP */}
        <path d={tipPath} fill={`url(#${g('tip')})`}
          stroke="rgba(100,110,130,0.45)" strokeWidth="0.5" strokeLinejoin="round" />
        {/* Tip opening hole */}
        <ellipse cx={MID} cy={TIP_Y + 3.5} rx={1.5} ry={1.5} fill="rgba(20,24,36,0.70)" />
        {/* Tip highlight */}
        <line x1={MID - 1.5} y1={TIP_Y + 5} x2={MID - 1.5} y2={TIP_Y + TIP_H - 2}
          stroke="rgba(255,255,255,0.35)" strokeWidth="0.6" />

      </svg>
    </div>
  )
}
